import Seo from "../../components/Seo";
import { Reveal, Eyebrow, PageHero, CtaBand } from "../../components/Shared";
import { photo } from "../../data/site";
import { Phone, Eye, Sun, Thermometer, X, Check } from "lucide-react";
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { BUSINESS } from "../../data/site";

export default function Tinting() {
  return (
    <>
      <Seo
        title="Automotive Window Tinting in Sioux Falls, SD | Free Quote — A+ Detailing"
        description="Professional automotive window tinting in Sioux Falls, SD. Heat rejection, glare reduction and added privacy — priced per vehicle with a free quote from A+ Detailing."
        path="/window-tinting"
      />
      <PageHero
        eyebrow="Window Tinting"
        lines={["COOL.", "CALM.", "PRIVATE."]}
        lead="Professional automotive window tint, quoted for your exact vehicle. Call or request a free quote and we'll recommend the right film for your vehicle and budget."

        chips={["GeoShield window film", "Lifetime warranty", "Free quote"]}
        testPrefix="tint"
      />

      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-20 sm:py-28 grid lg:grid-cols-2 gap-14 items-center">
        <div>
          <Reveal><Eyebrow>What good tint does</Eyebrow></Reveal>
          <Reveal delay={0.08}>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl font-extrabold tracking-tight">Comfort you can feel.</h2>
          </Reveal>
          <div className="mt-10 space-y-5">
            {[
              { icon: Thermometer, t: "Heat rejection", d: "Cut the South Dakota summer sun and keep your cabin noticeably cooler." },
              { icon: Sun, t: "Glare reduction", d: "Easier, safer driving into low sun — morning and evening." },
              { icon: Eye, t: "Privacy & looks", d: "A deeper, cleaner look with added privacy for passengers and belongings." },
            ].map((b, i) => (
              <Reveal key={b.t} delay={0.1 + i * 0.08}>
                <div className="flex gap-5 rounded-2xl border border-white/10 bg-card p-6" data-testid={`tint-benefit-${i}`}>
                  <span className="shrink-0 flex h-11 w-11 items-center justify-center rounded-xl bg-bright/10 border border-bright/20 text-bright">
                    <b.icon size={19} />
                  </span>
                  <div>
                    <h3 className="font-display text-lg font-bold">{b.t}</h3>
                    <p className="mt-1.5 text-sm text-steel leading-relaxed">{b.d}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
        <Reveal delay={0.15}>
          <div className="rounded-2xl border border-white/10 bg-card p-9 sm:p-12 text-center" data-testid="tint-quote-card">
            <h2 className="font-display text-3xl font-extrabold tracking-tight">Free tint quote</h2>
            <p className="mt-4 text-steel leading-relaxed">
              Tint pricing depends on your vehicle and the film you choose. Tell us
              what you drive and we'll quote it — no obligation.
            </p>
            <div className="mt-8 flex flex-col gap-3.5">
              <a
                href={BUSINESS.phoneHref}
                data-testid="tint-call-button"
                className="flex items-center justify-center gap-2 rounded-full bg-bright hover:bg-accent text-white font-semibold px-7 py-3.5 transition-colors"
              >
                <Phone size={16} /> Call {BUSINESS.phone}
              </a>
              <a
                href="/book"
                data-testid="tint-quote-button"
                className="flex items-center justify-center gap-2 rounded-full border border-white/20 hover:border-bright hover:text-bright text-white font-semibold px-7 py-3.5 transition-colors"
              >
                Request a Free Quote
              </a>
            </div>
          </div>
        </Reveal>
      </section>

      <FilmSection />

      <CtaBand testPrefix="tint" />
    </>
  );
}

const FILMS = [
  {
    slug: "apex-ultra",
    name: "Apex Ultra",
    tier: "Flagship",
    tser: "49–70%",
    irr: "95% (all shades)",
    uv: "99%+",
    construction: "2 mil dual-layer super ceramic",
    look: "Neutral carbon-inspired cool tone, ultra optical clarity",
    desc: "GeoShield's flagship — stacked ceramic layers built for maximum heat rejection and ultra optical clarity.",
    benefits: [
      "Dual ceramic layers deliver the highest IR heat rejection in the lineup",
      "Ultra optical clarity with a refined, neutral cool tone",
      "Zero signal interference — safe for GPS, Bluetooth and radio",
      "2 mil thickness for durability and precision",
    ],
  },
  {
    slug: "pro-nano",
    name: "Pro Nano",
    tier: "Premium ceramic",
    tser: "49–66%",
    irr: "76–83%",
    uv: "99%+",
    construction: "2 mil dyed ceramic, non-metalized",
    look: "Rich, deep warm black — no strong blue hue in lighter shades",
    desc: "Ceramic heat performance with the smooth handling and deep black tone of a dyed film.",
    benefits: [
      "Ceramic IR rejection for solid heat and glare control without reflective shine",
      "Deep, rich black tone with a warm, luxury finish",
      "99%+ UV rejection to help preserve interiors and protect skin",
      "Non-metalized — no interference with GPS, Bluetooth or mobile tech",
    ],
  },
  {
    slug: "c2-ceramic",
    name: "C2 Ceramic",
    tier: "Ceramic-carbon hybrid",
    tser: "35–63%",
    irr: "71–85%",
    uv: "99%+",
    construction: "Carbon pigmented ceramic",
    look: "Neutral carbon tone with premium comfort",
    desc: "The solar performance of ceramic technology combined with the color stability and neutral tone of carbon pigments.",
    benefits: [
      "Outstanding infrared heat rejection",
      "Glare-reducing finish that doesn't fade or interfere with signals",
      "99%+ UV protection",
      "Color-stable carbon pigments keep the tone true over time",
    ],
  },
  {
    slug: "c2-carbon",
    name: "C2 Carbon",
    tier: "Best value",
    tser: "30–42%",
    irr: "34–83% (by shade)",
    uv: "99%+",
    construction: "Carbon pigmented dyed film",
    look: "Rich, neutral black tone",
    desc: "The value pick — a noticeable upgrade over traditional dyed films without stepping into premium ceramic territory.",
    benefits: [
      "Carbon-infused construction performs and lasts beyond standard dyed films",
      "Moderate heat rejection for a cooler cabin feel",
      "Blocks 99%+ of harmful UV rays",
      "Color-stable — maintains deep, true color without fading or purpling",
    ],
  },
];

const COMPARISON = (film) => {
  const base = FILMS.find((f) => f.slug === "c2-carbon");
  const isBase = film.slug === "c2-carbon";
  return [
    { k: "Heat rejection (TSER)", a: film.tser, b: isBase ? "Traditional dyed films" : base.tser },
    { k: "Infrared (IR) rejection", a: film.irr, b: isBase ? "Lower than carbon" : base.irr },
    { k: "UV protection", a: film.uv, b: base.uv },
    { k: "Construction", a: film.construction, b: isBase ? "Standard dyed film" : base.construction },
    { k: "Warranty", a: "Lifetime", b: isBase ? "— conventional films fade & purple" : "Lifetime" },
    { k: "Signal safe", a: "Yes — non-metalized", b: isBase ? "Varies" : "Yes — non-metalized" },
  ];
};

function FilmSection() {
  const [active, setActive] = useState(null);
  return (
    <>
      <section className="bg-coal border-y border-white/5 py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-12">
            <div>
              <Reveal><Eyebrow>The film we install</Eyebrow></Reveal>
              <Reveal delay={0.08}>
                <h2 className="mt-4 font-display text-3xl sm:text-4xl font-extrabold tracking-tight">GeoShield automotive films</h2>
              </Reveal>
              <Reveal delay={0.14}>
                <p className="mt-4 max-w-xl text-sm text-steel leading-relaxed">
                  We install GeoShield window film — every line blocks 99%+ of UV rays and carries a lifetime warranty. Tap any film for its full heat-rejection facts and how it compares to our value option.
                </p>
              </Reveal>
            </div>
            <Reveal delay={0.2}>
              <img src="/geoshield-logo.svg" alt="GeoShield window film logo" className="h-14 w-auto" loading="lazy" />
            </Reveal>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {FILMS.map((f, i) => (
              <Reveal key={f.slug} delay={i * 0.06}>
                <button
                  data-testid={`tint-film-card-${f.slug}`}
                  onClick={() => setActive(f)}
                  className="group h-full w-full text-left rounded-2xl border border-white/10 bg-card p-7 transition-all duration-300 hover:border-bright/50 hover:-translate-y-1 hover:bg-cardhover"
                >
                  <span className="inline-block border border-bright/30 bg-bright/10 text-bright text-[10px] font-mono uppercase tracking-wider rounded-full px-2.5 py-1">
                    {f.tier}
                  </span>
                  <h3 className="mt-4 font-display text-xl font-bold">{f.name}</h3>
                  <p className="mt-3 font-mono text-xs text-bright">{f.tser} TSER</p>
                  <p className="mt-2 text-sm text-steel leading-relaxed">{f.desc}</p>
                  <span className="mt-4 inline-flex items-center gap-1.5 text-xs font-medium text-bright">
                    View benefits & comparison
                    <Eye size={12} />
                  </span>
                </button>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <p className="mt-6 text-xs text-muted">
              Film specs per GeoShield's published data. VLT shades available from 2% to 75% depending on the film line — we'll help you pick the right shade.
            </p>
          </Reveal>
        </div>
      </section>

      <AnimatePresence>
        {active && (
          <motion.div
            data-testid="film-detail-modal"
            className="fixed inset-0 z-[90] flex items-center justify-center bg-black/85 backdrop-blur-sm p-4 sm:p-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setActive(null)}
          >
            <motion.div
              className="relative w-full max-w-3xl max-h-[88vh] overflow-y-auto rounded-2xl border border-white/10 bg-coal p-7 sm:p-10"
              initial={{ scale: 0.95, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.96, opacity: 0 }}
              transition={{ duration: 0.25 }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                data-testid="film-modal-close"
                onClick={() => setActive(null)}
                aria-label="Close film details"
                className="absolute top-5 right-5 p-2 rounded-full glass border border-white/10 text-white hover:bg-bright transition-colors"
              >
                <X size={18} />
              </button>

              <span className="inline-block border border-bright/30 bg-bright/10 text-bright text-[10px] font-mono uppercase tracking-wider rounded-full px-3 py-1">
                {active.tier}
              </span>
              <h3 className="mt-4 font-display text-3xl font-extrabold tracking-tight">{active.name}</h3>
              <p className="mt-3 text-sm text-steel leading-relaxed">{active.desc}</p>

              <div className="mt-7 grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="film-spec-facts">
                {[
                  { k: "Heat rejection (TSER)", v: active.tser },
                  { k: "IR rejection", v: active.irr },
                  { k: "UV protection", v: active.uv },
                  { k: "Warranty", v: "Lifetime" },
                ].map((s) => (
                  <div key={s.k} className="rounded-xl border border-white/10 bg-card p-4">
                    <p className="font-mono text-[9px] uppercase tracking-widest text-muted">{s.k}</p>
                    <p className="mt-1.5 font-display text-lg font-bold text-white">{s.v}</p>
                  </div>
                ))}
              </div>

              <p className="mt-7 font-mono text-[11px] uppercase tracking-widest text-bright">Benefits</p>
              <ul className="mt-4 space-y-2.5">
                {active.benefits.map((b) => (
                  <li key={b} className="flex items-start gap-3 text-sm text-steel leading-relaxed">
                    <Check size={14} className="text-bright mt-0.5 shrink-0" /> {b}
                  </li>
                ))}
              </ul>
              <p className="mt-4 text-xs text-muted">Look & finish: {active.look} · {active.construction}</p>

              <p className="mt-8 font-mono text-[11px] uppercase tracking-widest text-bright">
                {active.slug === "c2-carbon" ? "C2 Carbon vs traditional dyed film" : `${active.name} vs C2 Carbon — our value film`}
              </p>
              <div className="mt-4 rounded-xl border border-white/10 overflow-hidden" data-testid="film-comparison-table">
                {COMPARISON(active).map((row, i) => (
                  <div key={row.k} className={`grid grid-cols-[1.1fr_1fr_1fr] gap-3 px-4 sm:px-5 py-3.5 ${i % 2 ? "bg-white/[0.02]" : ""} ${i ? "border-t border-white/5" : ""}`}>
                    <span className="text-xs text-muted">{row.k}</span>
                    <span className="text-xs font-semibold text-white">{row.a}</span>
                    <span className="text-xs text-steel">{row.b}</span>
                  </div>
                ))}
                <div className="grid grid-cols-[1.1fr_1fr_1fr] gap-3 px-4 sm:px-5 py-3 border-t border-white/10 bg-bright/5">
                  <span className="text-xs text-bright font-mono">Film</span>
                  <span className="text-xs font-semibold text-bright">{active.name}</span>
                  <span className="text-xs text-steel">{active.slug === "c2-carbon" ? "Dyed film" : "C2 Carbon"}</span>
                </div>
              </div>

              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <a href={BUSINESS.phoneHref} data-testid="film-modal-call" className="flex-1 flex items-center justify-center gap-2 rounded-full bg-bright hover:bg-accent text-white font-semibold px-6 py-3 text-sm transition-colors">
                  <Phone size={15} /> Call {BUSINESS.phone}
                </a>
                <a href="/book" data-testid="film-modal-quote" className="flex-1 flex items-center justify-center gap-2 rounded-full border border-white/20 hover:border-bright hover:text-bright text-white font-semibold px-6 py-3 text-sm transition-colors">
                  Get a Free Quote
                </a>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
